// Champagne — adult-birthday paper-invitation catalog card.
//
// A sophisticated, sparkler-dust preview. Outer frame is a gradient foil
// (accent → cream → accent) holding a paper card. Inside: TUG'ILGAN KUN
// eyebrow flanked by hairlines, an oval rose-tinted photo medallion ringed
// by champagne-flute silhouette accents, the name in the template font,
// and a small-caps date + venue caption. All colors from LayoutCtx.
import { Photo, type LayoutCtx } from "./_shared";

function MiniFlute({ color, ink, flip = false }: { color: string; ink: string; flip?: boolean }) {
  return (
    <svg
      viewBox="0 0 60 220"
      style={{ width: "100%", height: "100%", display: "block", transform: flip ? "scaleX(-1)" : undefined }}
      preserveAspectRatio="xMidYMid meet"
      aria-hidden
    >
      <path d="M 18 16 Q 18 70 28 100 L 32 100 Q 42 70 42 16 Z" fill={color} opacity={0.78} />
      <path d="M 22 30 Q 22 64 30 92 Q 38 64 38 30 Z" fill={ink} opacity={0.14} />
      <circle cx="28" cy="46" r="1.4" fill={color} opacity={0.45} />
      <circle cx="33" cy="58" r="1.1" fill={color} opacity={0.35} />
      <circle cx="27" cy="70" r="0.9" fill={color} opacity={0.3} />
      <path d="M 18 16 Q 30 22 42 16" stroke={ink} strokeWidth="0.6" fill="none" opacity={0.4} />
      <rect x="29" y="100" width="2" height="92" fill={color} opacity={0.85} />
      <ellipse cx="30" cy="196" rx="14" ry="3" fill={color} opacity={0.85} />
    </svg>
  );
}

export function ChampagneLayout({ names, date, venue, accent, ink, bg, font, photo, photos }: LayoutCtx) {
  const heroPhoto = photo || photos[0];
  const FOIL = `linear-gradient(135deg, ${accent} 0%, ${bg} 22%, ${accent}aa 45%, ${bg} 65%, ${accent} 100%)`;
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: FOIL,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          position: "relative",
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
        }}
      >
        {/* Diamond sparkle dust — deterministic positions */}
        <div aria-hidden style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          {[
            [10, 14, 5], [86, 18, 4], [14, 84, 4], [88, 80, 5],
            [50, 6, 3], [50, 96, 3], [6, 50, 3], [94, 50, 3],
          ].map(([l, t, s], i) => (
            <svg key={i} width={s + 4} height={s + 4} viewBox="0 0 16 16" style={{ position: "absolute", left: `${l}%`, top: `${t}%`, opacity: 0.55 }} aria-hidden>
              <path d="M8 1 L11 8 L8 15 L5 8 Z" stroke={accent} strokeWidth="0.7" fill="none" />
              <circle cx="8" cy="8" r="1" fill={accent} />
            </svg>
          ))}
        </div>

        {/* Eyebrow */}
        <div
          style={{
            position: "relative",
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "5%",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <span style={{ width: 14, height: 1, background: accent }} />
          TUG&apos;ILGAN KUN
          <span style={{ width: 14, height: 1, background: accent }} />
        </div>

        {/* Photo row — flute · medallion · flute */}
        <div
          style={{
            position: "relative",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "4%",
          }}
        >
          <div aria-hidden style={{ width: "12%", height: "auto", transform: "rotate(-7deg)" }}>
            <MiniFlute color={accent} ink={ink} />
          </div>

          <div
            style={{
              width: "55%",
              aspectRatio: "1 / 1",
              borderRadius: "50%",
              overflow: "hidden",
              position: "relative",
              border: `1px solid ${accent}88`,
              boxShadow: `inset 0 0 0 4px ${bg}, inset 0 0 0 5px ${accent}55`,
              background: `radial-gradient(circle at 50% 40%, ${accent}33, ${bg})`,
            }}
          >
            <Photo
              src={heroPhoto}
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
              fallback={
                <div style={{ position: "absolute", inset: 0, background: `radial-gradient(circle at 50% 40%, ${accent}55, ${bg})` }} />
              }
            />
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: `linear-gradient(180deg, ${accent}10 0%, transparent 50%, ${accent}25 100%)`,
                mixBlendMode: "multiply",
              }}
            />
          </div>

          <div aria-hidden style={{ width: "12%", height: "auto", transform: "rotate(7deg)" }}>
            <MiniFlute color={accent} ink={ink} flip />
          </div>
        </div>

        {/* Name — template font, italic */}
        <div
          style={{
            position: "relative",
            fontFamily: font,
            fontStyle: "italic",
            fontSize: 30,
            fontWeight: 400,
            lineHeight: 1.05,
            color: ink,
            marginTop: "5%",
          }}
        >
          {names}
        </div>

        <div style={{ flex: 1 }} />

        <div
          style={{
            position: "relative",
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          TUG&apos;ILGAN KUN · {date}
        </div>
        <div
          style={{
            position: "relative",
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
